import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import FloatingBubbles from '../components/FloatingBubbles';
import GlassCard from '../components/GlassCard';
import JellyButton from '../components/JellyButton';
import { Download, Mail, RotateCcw, Home, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const Preview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const [hasPrinted, setHasPrinted] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const savedToGalleryRef = useRef(false);
  
  const photo = location.state?.photo;
  const frame = location.state?.frame;
  const photos = location.state?.photos;

  useEffect(() => {
    console.log('=== PREVIEW PAGE LOADED ===');
    console.log('location.state:', location.state);
    console.log('photo exists:', !!photo);
    console.log('photo preview:', photo?.substring(0, 100));
    console.log('frame:', frame?.name);
    console.log('original photos count:', photos?.length);
    
    if (!photo || !frame) {
      console.error('PREVIEW ERROR: Missing data - photo:', !!photo, 'frame:', !!frame);
      toast.error('Data tidak lengkap');
      setTimeout(() => navigate('/frame-select'), 2000);
      return;
    }
  }, [photo, frame, navigate]);

  useEffect(() => {
    if (!photo || !frame) return;
    if (savedToGalleryRef.current) return;
    savedToGalleryRef.current = true;

    axios.post(`${API}/gallery/save`, {
      photo_data: photo,
      frame_name: frame.name,
      frame_category: ''
    }).catch((error) => {
      console.error('Error saving to gallery:', error);
    });
  }, [photo, frame]);

  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown <= 0) {
      navigate('/');
      return;
    }

    const id = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null) return null;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [redirectCountdown, navigate]);

  const createShareLink = async () => {
    if (!photo || !frame) return null;
    const response = await axios.post(`${API}/share/create`, {
      photo_data: photo,
      frame_name: frame.name
    });
    const id = response.data?.share_id;
    const url = response.data?.share_url;
    if (typeof url === 'string' && url) {
      return { shareId: id || url.split('/').pop(), shareUrl: url };
    }
    if (typeof id === 'string' && id) {
      return { shareId: id, shareUrl: '' };
    }
    return null;
  };

  const handleDownload = () => {
    if (!photo) {
      toast.error('Foto tidak tersedia');
      return;
    }
    const link = document.createElement('a');
    link.href = photo;
    link.download = `glowbox-strip-${Date.now()}.jpg`;
    link.click();
    toast.success('Foto berhasil diunduh!');
  };

  const handleEmailSend = async () => {
    if (!email) {
      toast.error('Masukkan email!');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Email tidak valid!');
      return;
    }

    setIsSending(true);
    try {
      await axios.post(`${API}/send-photo`, {
        email,
        photo,
        frameName: frame.name
      });
      toast.success(`Terkirim ke ${email}!`);
      setEmail('');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Gagal mengirim email');
    } finally {
      setIsSending(false);
    }
  };

  const openBrowserPrint = (fitMode = 'contain') => {
    if (!photo) {
      toast.error('Foto tidak tersedia');
      return;
    }

    const safeFit = fitMode === 'cover' ? 'cover' : 'contain';

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch (e) {}
    };

    iframe.onload = () => {
      const w = iframe.contentWindow;
      if (!w) {
        cleanup();
        toast.error('Gagal memulai proses cetak');
        return;
      }

      const done = () => {
        try { w.removeEventListener('afterprint', done); } catch (e) {}
        cleanup();
      };

      try {
        w.addEventListener('afterprint', done);
      } catch (e) {}

      setTimeout(() => {
        try { w.focus(); } catch (e) {}
        try { w.print(); } catch (e) {}
        setTimeout(done, 2000);
      }, 150);
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      toast.error('Gagal memulai proses cetak');
      return;
    }

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Cetak Label GlowBox 54x80</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            @page { size: 54mm 80mm; margin: 0; }
            html, body { width: 54mm; height: 80mm; margin: 0; padding: 0; }
            body { display: flex; align-items: stretch; justify-content: stretch; background: white; }
            img { width: 54mm; height: 80mm; object-fit: ${safeFit}; display: block; }
          </style>
        </head>
        <body>
          <img src="${photo}" />
        </body>
      </html>
    `);
    doc.close();
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      if (!photo) {
        toast.error('Foto tidak tersedia');
        return;
      }

      setIsGeneratingShare(true);
      const link = await createShareLink().catch(() => null);
      if (link?.shareUrl) setShareUrl(link.shareUrl);
      setIsGeneratingShare(false);

      if (window?.GlowBox && typeof window.GlowBox.postMessage === 'function') {
        if (!link?.shareId) {
          toast.error('Gagal membuat link untuk print');
          return;
        }
        window.GlowBox.postMessage(JSON.stringify({ type: 'print_image', shareId: link.shareId }));
        toast.success('Print diproses');
        setHasPrinted(true);
        setRedirectCountdown(120);
        return;
      }

      openBrowserPrint('contain');
      toast.success('Print diproses');
      setHasPrinted(true);
      setRedirectCountdown(120);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Gagal mencetak');
    } finally {
      setIsGeneratingShare(false);
      setIsPrinting(false);
    }
  };

  const formatCountdown = (seconds) => {
    const total = typeof seconds === 'number' ? Math.max(0, seconds) : 0;
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSaveToGallery = async () => {
    try {
      await axios.post(`${API}/gallery/save`, {
        photo_data: photo,
        frame_name: frame.name,
        frame_category: ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Gagal menyimpan');
    }
  };

  const handleCustomize = () => {
    navigate('/customize', { state: { photo, frame, photos } });
  };

  const handleRetake = () => {
    navigate('/frame-select');
  };

  const handleHome = () => {
    navigate('/');
  };

  if (!photo || !frame) {
    return (
      <div className="candy-gradient-bg min-h-screen flex items-center justify-center overflow-x-hidden px-4 py-10">
        <GlassCard className="text-center p-8">
          <p className="text-gray-700 font-medium">Loading...</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="candy-gradient-bg min-h-screen py-6 px-4 overflow-x-hidden">
      <FloatingBubbles />
      
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <div className="text-center mb-4">
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-800 mb-1"
            style={{ fontFamily: 'Fredoka, cursive' }}
          >
            Foto Strip Siap! 🎉
          </motion.h2>
          <p className="text-sm text-gray-600 font-medium">
            Download, kirim, atau cetak!
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr,2fr] gap-4">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <GlassCard className="flex flex-col">
              <div className="bg-white p-3 rounded-xl shadow-xl">
                <img
                  src={photo}
                  alt="Final strip"
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                  data-testid="final-photo"
                />
              </div>
              <div className="mt-2 text-center flex-shrink-0">
                <p className="text-gray-800 font-bold">{frame.name}</p>
              </div>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col gap-3"
          >
            {hasPrinted ? (
              <>
                <GlassCard>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Hasil Print</h3>
                  <div className="bg-white p-2 rounded-xl shadow-sm">
                    <img
                      src={photo}
                      alt="Hasil print"
                      className="w-full max-h-[45vh] object-contain rounded-lg"
                    />
                  </div>
                </GlassCard>

                <GlassCard>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">QR Download</h3>
                  <div className="text-center">
                    {shareUrl ? (
                      <div className="bg-white p-3 rounded-lg inline-block">
                        <QRCodeCanvas value={shareUrl} size={140} level="H" />
                        <p className="text-[10px] text-gray-600 mt-2 max-w-[220px] break-all">
                          {shareUrl}
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-600 font-medium">
                        {isGeneratingShare ? 'Membuat QR code...' : 'QR code belum tersedia'}
                      </p>
                    )}
                  </div>
                </GlassCard>

                <GlassCard>
                  <h3 className="text-sm font-bold text-gray-800 mb-1">Redirect Otomatis</h3>
                  <p className="text-sm text-gray-700 font-semibold text-center">
                    {formatCountdown(redirectCountdown ?? 0)}
                  </p>
                  <p className="text-xs text-gray-600 font-medium text-center mt-1">
                    Kembali ke halaman awal
                  </p>
                  <div className="mt-3">
                    <JellyButton
                      onClick={handleHome}
                      variant="secondary"
                      className="w-full text-xs"
                    >
                      <Home className="w-4 h-4 inline mr-1" />
                      Home
                    </JellyButton>
                  </div>
                </GlassCard>
              </>
            ) : (
              <>
                <GlassCard>
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Print</h3>
                  <div className="flex flex-col gap-2">
                    <JellyButton
                      onClick={handlePrint}
                      disabled={isPrinting}
                      testId="print-photo-btn"
                      className="w-full text-xs inline-flex items-center justify-center gap-1"
                    >
                      <Printer className="w-4 h-4" />
                      {isPrinting ? 'Printing...' : 'Print Now'}
                    </JellyButton>
                  </div>
                </GlassCard>

                <div className="grid grid-cols-2 gap-2">
                  <JellyButton
                    onClick={handleCustomize}
                    variant="secondary"
                    testId="customize-btn"
                    className="text-xs"
                  >
                    ✏️ Edit
                  </JellyButton>
                  <JellyButton
                    onClick={handleRetake}
                    variant="secondary"
                    testId="retake-btn"
                    className="text-xs"
                  >
                    🔄 Retake
                  </JellyButton>
                </div>

                <JellyButton
                  onClick={handleHome}
                  variant="secondary"
                  testId="home-btn"
                  className="w-full text-sm"
                >
                  <Home className="w-4 h-4 inline mr-1" />
                  Home
                </JellyButton>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Preview;
